import { useMemo, useState } from 'react';
import { Button, Checkbox, Group, Loader, MultiSelect, Paper, Stack, Text, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import dayjs from 'dayjs';
import { useExportSmsReportMutation } from '@/hooks/reports';
import { usePaginatedChurchOptions } from '@/hooks/churches';
import type { SmsReportExportRequest } from '@/types/reports';
import styles from './Reports.module.sass';

const DATE_FORMAT = 'YYYY-MM-DD';

const buildDownloadFileName = ({ startDate, endDate }: SmsReportExportRequest) =>
  `sms-report-${startDate}-${endDate}.csv`;

const triggerBlobDownload = ({ blob, fileName }: { blob: Blob; fileName: string }) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

export const Reports = () => {
  const [isAllChurches, setIsAllChurches] = useState<boolean>(false);
  const [churchSearch, setChurchSearch] = useState<string>('');
  const [selectedChurchIds, setSelectedChurchIds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const { churchOptions, isSearchingChurches, isLoadingMoreChurches, scrollAreaProps } = usePaginatedChurchOptions({
    searchTerm: churchSearch,
    enabled: !isAllChurches,
  });

  const exportSmsReportMutation = useExportSmsReportMutation();

  const isDateRangeInvalid = useMemo(() => {
    if (!startDate || !endDate) {
      return false;
    }
    return dayjs(startDate).isAfter(dayjs(endDate), 'day');
  }, [endDate, startDate]);

  const canDownload = useMemo(() => {
    if (!startDate || !endDate || isDateRangeInvalid) {
      return false;
    }
    if (isAllChurches) {
      return true;
    }
    return selectedChurchIds.length > 0;
  }, [endDate, isAllChurches, isDateRangeInvalid, selectedChurchIds.length, startDate]);

  const handleAllChurchesChange = (checked: boolean) => {
    setIsAllChurches(checked);
    if (checked) {
      setSelectedChurchIds([]);
      setChurchSearch('');
    }
  };

  const handleDownloadReport = async () => {
    if (!canDownload || !startDate || !endDate) {
      return;
    }

    const payload: SmsReportExportRequest = {
      churchIds: isAllChurches ? null : selectedChurchIds,
      startDate: dayjs(startDate).format(DATE_FORMAT),
      endDate: dayjs(endDate).format(DATE_FORMAT),
    };

    try {
      const blob = await exportSmsReportMutation.mutateAsync(payload);
      triggerBlobDownload({ blob, fileName: buildDownloadFileName(payload) });
    } catch (error) {
      console.error('Failed to download SMS report:', error);
    }
  };

  return (
    <Stack mx="xxl" py="md" gap="md">
      <Title order={2}>Reports</Title>
      <Text className={styles.subtitle}>Download an SMS usage report by church and date range.</Text>

      <Paper withBorder p="lg" radius="md" className={styles.form}>
        <Stack gap="md">
          <Checkbox
            label="All Churches"
            checked={isAllChurches}
            onChange={(event) => handleAllChurchesChange(event.currentTarget.checked)}
          />

          <MultiSelect
            label="Churches"
            placeholder={isAllChurches ? 'All churches selected' : 'Search and select churches'}
            data={churchOptions}
            searchable
            searchValue={churchSearch}
            onSearchChange={setChurchSearch}
            value={selectedChurchIds}
            onChange={setSelectedChurchIds}
            rightSection={isSearchingChurches || isLoadingMoreChurches ? <Loader size="xs" /> : undefined}
            scrollAreaProps={scrollAreaProps}
            nothingFoundMessage="No churches found"
            disabled={isAllChurches}
            required={!isAllChurches}
          />

          <Group className={styles.dateFields} grow align="flex-start">
            <DatePickerInput
              label="Start date"
              placeholder="Select start date"
              value={startDate}
              onChange={setStartDate}
              valueFormat={DATE_FORMAT}
              maxDate={endDate ?? undefined}
              required
              error={isDateRangeInvalid ? 'Start date must be on or before end date' : undefined}
            />
            <DatePickerInput
              label="End date"
              placeholder="Select end date"
              value={endDate}
              onChange={setEndDate}
              valueFormat={DATE_FORMAT}
              minDate={startDate ?? undefined}
              required
              error={isDateRangeInvalid ? 'End date must be on or after start date' : undefined}
            />
          </Group>

          <Group justify="flex-end" mt="sm">
            <Button onClick={handleDownloadReport} disabled={!canDownload} loading={exportSmsReportMutation.isPending}>
              Download Report
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
};
